// assets/AllCourses/Exercises/CompleteTheConversationExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import SpeakerButton from "@/components/ui/SpeakerButton";
import useSpeech from "@/hooks/useSpeech";

export default function CompleteTheConversationExercise({
  exercise = {},
  onUpdate, // Only in edit mode
  errors = {},
  isPreview = false, // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {}, // Optional: for saving progress
}) {
  const isEditMode = typeof onUpdate === "function";
  const { speak, stop } = useSpeech();

  // === SAFE QUESTIONS ===
  const rawQuestions = exercise?.conversationQuestions;
  const questions = Array.isArray(rawQuestions) ? rawQuestions : [];

  // === STUDENT STATE ===
  const [answers, setAnswers] = useState({}); // { [qIndex]: string }
  const [hasInteracted, setHasInteracted] = useState(false);
  const completedRef = useRef(false);

  // Sync saved attempt in student mode
  useEffect(() => {
    if (!isPreview || !exercise?.attempt) return;

    setAnswers(exercise.attempt.answers || {});
    setHasInteracted(!!exercise.attempt.hasInteracted);
  }, [isPreview, exercise?.attempt]);

  // Reset completion flag
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  // Normalize for comparison
  const normalize = (text = "", requireCap = false) => {
    const t = String(text || "").trim();
    return requireCap ? t : t.toLowerCase();
  };

  // Get full text for speaker (question + answer)
  const getSpeakerText = (q) => {
    const lines = [];
    if (q.question) lines.push(q.question.trim());
    if (q.answer) lines.push(q.answer.trim());
    return lines.join("\n");
  };

  // === EDIT MODE FUNCTIONS ===
  const updateQuestions = (nextQuestions) => {
    if (isEditMode) onUpdate("conversationQuestions", nextQuestions);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = {
      ... (next[qIdx] || {
        question: "",
        answer: "",
        requireCapitalization: false,
      }),
      [field]: value,
    };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: !!checked,
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        question: "",
        answer: "",
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode || questions.length <= 1) return;
    stop();
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const handleInputChange = (qIndex, value) => {
    if (isEditMode || showResult) return;

    const updated = { ...answers, [qIndex]: value };
    setAnswers(updated);
    setHasInteracted(true);

    // Save progress
    if (typeof setAttempt === "function") {
      setAttempt({ answers: updated, hasInteracted: true });
    }

    // Check if all questions are answered
    const allAnswered = questions.every((_, i) => 
      updated[i] !== undefined && updated[i].trim() !== ""
    );

    if (allAnswered && !completedRef.current) {
      completedRef.current = true;
      checkAnswers(updated);
    }
  };

  const checkAnswers = (finalAnswers) => {
    const allCorrect = questions.every((q, idx) => {
      const user = normalize(finalAnswers[idx] || "", q.requireCapitalization);
      const correct = normalize(q.answer || "", q.requireCapitalization);
      return user === correct;
    });

    onComplete(exercise._id, allCorrect);
  };

  // === ADMIN EDIT MODE ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No conversation lines yet. Click "Add Line" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <TextareaField
                  label={`Conversation Line ${qIdx + 1}`}
                  value={q.question || ""}
                  onChange={(v) => handleQuestionChange(qIdx, "question", v)}
                  placeholder="A: Hello! How are you?\nB: I'm fine, thank you. And _____?"
                  rows={6}
                  required
                  error={errors?.[`exercise-${exercise.index}-conv-question-${qIdx}`]}
                />
              </div>

              <SpeakerButton
                text={getSpeakerText(q)}
                lang="en-GB"
                size="lg"
                onPlay={() => speak(getSpeakerText(q), "en-GB")}
                onStop={stop}
              />
            </div>

            <StringField
              label="Correct Answer (what student should type)"
              value={q.answer || ""}
              onChange={(v) => handleQuestionChange(qIdx, "answer", v)}
              placeholder="you"
              required
              error={errors?.[`exercise-${exercise.index}-conv-answer-${qIdx}`]}
            />

            <CapitalizationRule
              checked={!!q.requireCapitalization}
              onChange={(c) => handleCapitalizationChange(qIdx, c)}
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
              disabled={questions.length === 1}
            >
              Remove Line
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Conversation Line
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (questions.length === 0) {
    return (
      <Card className="max-w-3xl mx-auto">
        <CardHeader>
          <CardDescription>
            No conversation lines available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>
          Complete the conversation by typing the missing word(s).
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIndex) => {
          const userAnswer = answers[qIndex] || "";
          const isCorrect =
            showResult &&
            normalize(userAnswer, q.requireCapitalization) ===
              normalize(q.answer || "", q.requireCapitalization);

          return (
            <div key={qIndex} className="space-y-6">
              {/* Conversation Display */}
              <pre className="whitespace-pre-wrap font-sans text-lg leading-relaxed bg-muted/50 p-6 rounded-xl">
                {q.question || "Conversation missing."}
              </pre>

              {/* Speaker Button */}
              <div className="flex justify-center">
                <SpeakerButton
                  text={getSpeakerText(q)}
                  lang="en-GB"
                  size="lg"
                  onPlay={() => speak(getSpeakerText(q), "en-GB")}
                  onStop={stop}
                />
              </div>

              {/* Answer Input */}
              <div className="max-w-md mx-auto">
                <Input
                  value={userAnswer}
                  onChange={(e) => handleInputChange(qIndex, e.target.value)}
                  placeholder="Type your answer here..."
                  disabled={showResult}
                  className="text-center text-lg font-medium"
                />

                {/* Feedback */}
                {showResult && (
                  <div className="mt-4 text-center">
                    {isCorrect ? (
                      <p className="text-green-700 text-xl font-semibold">
                        Correct! ✅
                      </p>
                    ) : (
                      <p className="text-red-700 text-xl font-semibold">
                        Incorrect — Correct answer: <strong>{q.answer}</strong> ❌
                      </p>
                    )}
                    <p className="mt-2 text-base">
                      You wrote: <strong>"{userAnswer || "(empty)"}"</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Final Completion Message */}
        {showResult && (
          <div className="text-center py-8">
            <p className="text-2xl font-bold text-blue-700">
              Exercise Complete! 🎉
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}